import React, { createElement, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { days, ScheduleData, Staff, getDateOfWeekday, ScheduleMetadata, CustomEvent, JobRoleForShift, isDateInRange, getTimeOffMessage, updateScheduleMetadataWithStaffCount, initialDataScheduleMetadataMinimumEmployeeCount, StaffRequirement, getStaffAvailabilityForTheDay, updateDragDropShift, manualUpdateDragDropShift, checkIfStaffHasSameShift, ExtendedShift, checkIfShiftAm } from '../../helpers/workTimeScheduleHelper'
import { getFirstLetterUpperCase } from '../../helpers/utils'
import { getUserRoles } from '../../helpers/userRolesHelper'
import { JobRole, PerDayCountForListing, PrimeReactDropDown, StyleDetails, ToastMessageProps } from '../../constants/staticTypes'
import { JTranslation } from '../../helpers/jTranslate/JTranslation'
import { NO_ROLES_ASSIGNED } from '../../constants/strings'
import { useQueryClient } from 'react-query'
import { STAFFS_WITH_SCHEDULES_AND_SHIFT, USER_ROLES } from '../../constants/queryKeys'
import CustomEventForm from './CustomEventForm'
import CustomTooltip from './CustomTooltip'
import dayjs from 'dayjs'
import useMutationHook from '../../hooks/useMutationHook'
import useQueryHook from '../../hooks/useQueryHook'
import UserAvatar from './UserAvatar'
import TimeOffCard from './TimeOffCard'
import { Divider } from 'primereact/divider'
import { Badge } from 'primereact/badge'
import { capitalize, create } from 'lodash'
import { closestCenter, DndContext, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import ShiftDroppable from './shiftDroppable'
import ShiftDraggable from './shiftDraggable'
import { Dropdown } from 'antd'
import AddMultipleShiftAssignmentModal from './AddMultipleShiftAssignmentModal'
import DeleteShiftModal from './DeleteShiftModal'

type Props = {
    reloadPageData: () => void
    saveEmployeeName: (name: string) => void
    scheduleId: string
    selectedWeek: dayjs.Dayjs
    setData: Dispatch<SetStateAction<Staff | null>>,
    setSelectedDay: Dispatch<SetStateAction<string>>
    setShiftSideBar: React.Dispatch<React.SetStateAction<boolean>>
    setToastMessage: Dispatch<SetStateAction<ToastMessageProps>>
    shiftSideBar: boolean,
    staffingRequirement: PerDayCountForListing[]
    staffsWithSchedule: ScheduleData | null
    scheduleMetadata: ScheduleMetadata | null
    selectedUsers: PrimeReactDropDown[]
    setSelectedUsers: Dispatch<SetStateAction<PrimeReactDropDown[]>>
    isLoading: boolean
    isDateOlder: boolean
    amPmView: boolean
}

const ShiftScheduler = ({
    reloadPageData,
    saveEmployeeName,
    scheduleId,
    selectedWeek,
    setData,
    setShiftSideBar,
    setToastMessage,
    shiftSideBar,
    staffingRequirement,
    staffsWithSchedule,
    setSelectedDay,
    scheduleMetadata,
    selectedUsers,
    setSelectedUsers,
    isLoading,
    isDateOlder,
    amPmView
}: Props) => {
    const queryClient = useQueryClient()
    const scheduleMutation = useMutationHook(queryClient, true)
    const shiftDragDropMutation = useMutationHook(queryClient, false)
    const { staffs, schedule, holidays, mandatoryDays } = staffsWithSchedule!
    const totalEvents = scheduleMetadata?.customEvents?.map(event => event?.eventData)?.flat().length ?? 0
    const toolTipTimers = useRef<{ [key: number]: ReturnType<typeof setTimeout> | null }>({});

    const [rolesList, setRolesList] = useState<JobRole[]>([]); // roles list
    const [staffingRequirementWithMinStaff, setStaffingRequirementWithMinStaff] = useState<StaffRequirement[]>(initialDataScheduleMetadataMinimumEmployeeCount)
    const [hoveredRow, setHoveredRow] = useState<string | null>(null);
    const [allUsers, setAllUsers] = useState<PrimeReactDropDown[]>([])
    const [shiftDroppedId, setShiftDroppedId] = useState<string | null>(null)
    const [blockLoading, setBlockLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<Staff[]>([])
    const [assignStaffShift, setAssignStaffShift] = useState<ExtendedShift>()
    const [deleteStaffShift, setDeleteStaffShift] = useState<ExtendedShift>()

    // Configure sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Minimum movement distance before drag starts
                delay: 300, // Delay before drag starts
                tolerance: 5,
            },
        }),
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5, // Minimum movement distance before drag starts
                delay: 300, // Delay before drag starts
                tolerance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                distance: 5, // Minimum movement distance before drag starts
                delay: 300, // Delay before drag starts
            },
        }),
    );

    useEffect(() => {
        // if (!staffs.length) return; 

        const allStaffs: PrimeReactDropDown[] = staffs?.map((staff) => {
            const { id, preferredName, jobRoles = [] } = staff

            let isAvailableForSchedule = true;
            if (jobRoles.length === 1) {
                isAvailableForSchedule = 'isAvailableForSchedule' in jobRoles[0]
                    ? !!jobRoles[0].isAvailableForSchedule
                    : true;
            }

            return { code: id, name: capitalize(preferredName ?? ''), isAvailableForSchedule }
        })
        setAllUsers(allStaffs);
        setTableData(staffs);
    }, [staffs])

    // roles list
    useQueryHook(USER_ROLES, getUserRoles, (res) => {
        let filteredData = res.data?.data.lists.filter((item: any) => item.isAvailableForSchedule)

        const sortedRoles = filteredData.sort((a: any, b: any) =>
            a.jobRoleName.localeCompare(b.jobRoleName)
        );

        setRolesList(sortedRoles);
    });

    useEffect(() => {
        if (rolesList.length && scheduleMetadata && staffingRequirement) {
            const { staffRequirement } = scheduleMetadata

            const listWithMinCount = updateScheduleMetadataWithStaffCount({
                rolesList: rolesList,
                staffRequirement: staffRequirement,
                staffingRequirement: staffingRequirement
            });

            setStaffingRequirementWithMinStaff(listWithMinCount)
        }
    }, [rolesList, scheduleMetadata, staffingRequirement]);

    const getRoleColorCode = (roleId: string): StyleDetails => {
        const role = rolesList?.find(role => role.id === roleId)
        if (role?.styleDetails) {
            return role.styleDetails
        } else {
            return {
                backgroundColor: '#87b6fb',
                color: '#262930'
            }
        }
    }

    const onSuccess = (message: string, variant: string) => {
        setToastMessage({ message, variant, show: true })
        reloadPageData()
    }

    const onError = (message: string, variant: string) => {
        setToastMessage({ message, variant, show: true })
    }

    const staffNameTemplate = (rowData: Staff) => {
        const { id, preferredName = '', jobRoles = [], photoThumbnail } = rowData
        const checked = selectedUsers.some((user) => user.code === id)
        const roles = jobRoles
            .map((role: JobRoleForShift | undefined) => role?.jobRole?.jobRoleName)
            .filter(Boolean)
            .join(', ');

        let isAvailableForSchedule = true;
        if (jobRoles.length === 1) {
            isAvailableForSchedule = 'isAvailableForSchedule' in jobRoles[0]
                ? !!jobRoles[0].isAvailableForSchedule
                : true;
        }
        const userOptions = { code: id, name: capitalize(preferredName ?? ''), isAvailableForSchedule };

        return (
            <div
                key={id}
                className="text-nowrap d-flex align-items-center"
                onMouseEnter={() => setHoveredRow(id)}  // Track hovered row ID
                onMouseLeave={() => setHoveredRow(null)} // Reset when mouse leaves
            >
                {/* Conditionally display checkbox on hover */}
                {(hoveredRow === id || checked) && (
                    <div className="select-staff-checkbox">
                        <Checkbox
                            onChange={(e) => {
                                if (e.checked) {
                                    setSelectedUsers([...selectedUsers, userOptions])
                                } else {
                                    setSelectedUsers(selectedUsers.filter((user) => user.code !== id))
                                }
                            }}
                            checked={checked}
                        />
                    </div>
                )}

                {/* Avatar */}
                {hoveredRow !== id && !checked && (
                    <UserAvatar imageUrl={photoThumbnail} name={preferredName} />
                )}

                {/* User Details */}
                <div style={{ width: '10rem' }}>
                    <div className="overflow-ellipsis fw-bold">
                        <JTranslation typeCase="pascal" text={preferredName} />
                    </div>
                    <div className="overflow-ellipsis" title={roles}>
                        <JTranslation
                            typeCase="pascal"
                            text={roles || NO_ROLES_ASSIGNED}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const cellBodyTemplate = ({ day, rowData }: { day: string; rowData: Staff }) => {
        const { shifts, preferredName, unavailableDays, availabilities } = rowData
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
        const availabilityText = getStaffAvailabilityForTheDay({ day, availabilities, selectedWeek, type: 'schedule' })

        const unavailabilityArray = unavailableDays.filter((unavailableDay) => isDateInRange({ date: date, startDate: unavailableDay.startDate, endDate: unavailableDay.endDate }))
        const unavailability = unavailabilityArray.filter((item) => (item.actionStatus === 'APPROVED' || item.actionStatus === 'PENDING'))
        const shiftsArray = shifts[date]
        const isBusinessOpen = holidays.filter((holiday) => {
            const isSameDate = isDateInRange({ date: date, startDate: holiday.startDate, endDate: holiday.endDate })
            const isNotOpenForBusiness = holiday.isOpenForBusiness === false
            return isSameDate && isNotOpenForBusiness
        }).length > 0

        const timeOffText = getTimeOffMessage({ unavailability: unavailability, type: 'schedule', actionButton: true }, date)?.length
            ? <TimeOffCard key={`time-off-1-${rowData?.id}-${day}-${date}`} unavailability={unavailability} type='schedule' actionButton={true} date={date} />
            : availabilityText

        if (schedule.actionStatus === 'draft' && !Object.keys(shifts).length && !isDateOlder) {
            return (
                timeOffText ? (
                    <span key={`${rowData?.id}-${day}-${date}`}>
                        {timeOffText}
                        <div
                            className={`small-add-button`}
                            onClick={() => {
                                setShiftSideBar(!shiftSideBar)
                                saveEmployeeName(rowData.preferredName)
                                setSelectedDay(day)
                                setData(rowData)
                            }}
                        >
                            <i className="ri-add-line"></i>
                        </div>
                    </span>
                ) : (
                    <div className='d-flex' key={`${rowData?.id}-${day}-${date}`}>
                        <span
                            className={`${unavailability ? 'unavailable' : ''}`}
                        >
                            {
                                getTimeOffMessage({ unavailability: unavailability, type: 'schedule' }, date)?.length
                                    ? <TimeOffCard key={`time-off-2-${rowData?.id}-${day}-${date}`} unavailability={unavailability} type='schedule' actionButton={true} date={date} />
                                    : getStaffAvailabilityForTheDay({ day, availabilities, selectedWeek })
                            }
                        </span>
                        <div
                            className={`scheduler-cell-body`}
                            onClick={() => {
                                setShiftSideBar(!shiftSideBar)
                                saveEmployeeName(rowData.preferredName)
                                setSelectedDay(day)
                                setData(rowData)
                            }}
                        >
                            <i className="ri-add-line"></i>
                        </div>
                    </div>)
            )
        }

        if (shiftsArray?.length) {
            // Sort the shiftsArray based on startTime and endTime 
            const sortedShifts = shiftsArray.sort((a, b) => {
                const startA = dayjs(a.assignedShifts.startTime, "hh:mm A");
                const startB = dayjs(b.assignedShifts.startTime, "hh:mm A");
                const endA = dayjs(a.assignedShifts.endTime, "hh:mm A");
                const endB = dayjs(b.assignedShifts.endTime, "hh:mm A");

                // Compare start times first; if they're equal, compare end times
                if (startA.isSame(startB)) {
                    return endA.isBefore(endB) ? -1 : 1;
                }
                return startA.isBefore(startB) ? -1 : 1;
            });

            const shiftTemplate = createElement('span', { children: [<></>] });
            const shiftAMTemplate = createElement('div', { className: 'w-100 flex-grow-1 d-flex flex-column align-items-center', children: [<></>] });
            const shiftPMTemplate = createElement('div', { className: 'w-100 flex-grow-1 d-flex flex-column align-items-center', children: [<></>] });
            const newShiftButton = (
                <div
                    className={`small-add-button w-100`}
                    onClick={() => {
                        setShiftSideBar(!shiftSideBar);
                        saveEmployeeName(rowData.preferredName);
                        setSelectedDay(day);
                        setData(rowData);
                    }}
                >
                    <i className="ri-add-line"></i>
                </div>
            )

            sortedShifts.forEach((shift, index) => {
                const { assignedShifts } = shift;
                const { startTime, endTime, isBd, isClose, role, roleId } = assignedShifts;
                const styleDetails = getRoleColorCode(roleId);
                const { backgroundColor, color } = styleDetails;
                const isHighlighted = shift?.highlight === undefined || shift?.highlight === true;

                const parsedDate = getDateOfWeekday({ selectedWeek, day, format: 'YYYY-MM-DD' })
                const dragId = `${rowData?.id}_${shift?.id}_${day}_${parsedDate}`;
                const isAmShift = checkIfShiftAm(startTime)

                const startTimeParsed = dayjs(startTime, 'hh:mm A');
                const endTimeParsed = dayjs(endTime, 'hh:mm A');

                const shiftDraggable = (
                    <ShiftDraggable key={dragId} id={dragId} isDisabled={schedule?.actionStatus !== 'draft'}>
                        <CustomTooltip
                            key={`schedule-custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                            staffName={preferredName}
                            showDelay={500}
                            content={sortedShifts}
                            showOnlyHovered={true}
                            currentShift={shift}
                        >
                            <div
                                id={`custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                                className="scheduler-shift-card-body"
                                style={{
                                    backgroundColor: "white",
                                    border: `2px solid ${isHighlighted ? backgroundColor : '#8e8e8e'}`,
                                    opacity: `${isHighlighted ? '1' : '0.35'}`,
                                }}
                            >
                                <div className="d-flex">
                                    <div className="flex-grow-0 d-flex align-items-center justify-center">
                                        <span
                                            className="shift-role"
                                            style={{
                                                backgroundColor: isHighlighted ? backgroundColor : '#8e8e8e',
                                                color: isHighlighted ? color : '#fff',
                                            }}
                                        >
                                            {getFirstLetterUpperCase(role)}
                                        </span>
                                    </div>
                                    <div
                                        className="flex-grow-1 shift-name-main text-nowrap"
                                        onClick={() => {
                                            if (schedule.actionStatus === "draft" && !isDateOlder) {
                                                toolTipTimers.current[index] = setTimeout(() => {
                                                    setShiftSideBar(!shiftSideBar);
                                                    saveEmployeeName(rowData.preferredName);
                                                    setSelectedDay(day);
                                                    setData(rowData);
                                                }, 200);
                                            }
                                        }}
                                        role='button'
                                        tabIndex={0}
                                    >
                                        {amPmView 
                                            ? (
                                                <>
                                                    {isClose ? `${
                                                        startTimeParsed.minute() === 0 
                                                            ? startTimeParsed.format('h') 
                                                            : startTimeParsed.format('h:m')
                                                    } - CL` : ""}
                                                    {isBd ? `${
                                                        startTimeParsed.minute() === 0 
                                                            ? startTimeParsed.format('h') 
                                                            : startTimeParsed.format('h:m')
                                                    } - V` : ""}
                                                    {!isBd && !isClose ? `${
                                                        startTimeParsed.minute() === 0 
                                                            ? startTimeParsed.format('h') 
                                                            : startTimeParsed.format('h:m')
                                                    } - ${
                                                        endTimeParsed.minute() === 0 
                                                            ? endTimeParsed.format('h') 
                                                            : endTimeParsed.format('h:m')
                                                    }` : ""}
                                                </>
                                            )
                                            : (
                                                <>
                                                    {isClose ? `${startTime} - Close` : ""}
                                                    {isBd ? `${startTime} - V` : ""}
                                                    {!isBd && !isClose ? `${startTime} - ${endTime}` : ""}
                                                </>
                                            )
                                        }
                                    </div>
                                    {schedule.actionStatus === 'draft' && !isDateOlder &&
                                        <Dropdown
                                            trigger={['click']}
                                            menu={{
                                                items: [
                                                    {
                                                        key: index + 1,
                                                        icon: < i className="ri-user-add-line" style={{ fontSize: '1.25rem' }}></i>,
                                                        label: 'Assign Users',
                                                        onClick: () => setAssignStaffShift(shift)
                                                    },
                                                    {
                                                        key: index + 2,
                                                        icon: < i className="ri-delete-bin-line" style={{ fontSize: '1.25rem' }}></i>,
                                                        label: 'Delete',
                                                        onClick: (e) => setDeleteStaffShift( shift)
                                                    },
                                                ]
                                            }}
                                            className='h-100 flex-grow-0 d-flex align-items-center'
                                            onOpenChange={(visible) => {
                                                if (visible && toolTipTimers.current[index]) {
                                                    clearTimeout(toolTipTimers.current[index]);
                                                }
                                            }}
                                        >
                                            < i className="ri-more-2-fill" style={{ fontSize: '1.25rem' }}></i>
                                        </Dropdown>
                                    }
                                </div>
                            </div>
                        </CustomTooltip>
                    </ShiftDraggable>
                )

                if(amPmView) {
                    if(isAmShift) {
                        shiftAMTemplate.props.children.push(shiftDraggable);
                    } else {
                        shiftPMTemplate.props.children.push(shiftDraggable);
                    }
                } else {
                    shiftTemplate.props.children.push(shiftDraggable);
                }
            });

            return (
                <span key={`${rowData?.id}-${day}`}>
                    {timeOffText}

                    {amPmView 
                        ? <div className='d-flex gap-1'>{shiftAMTemplate}{shiftPMTemplate}</div> 
                        : shiftTemplate
                    }
                    
                    {schedule.actionStatus === "draft" && !isDateOlder ? (
                        <>
                            {amPmView 
                                ? (
                                    // <div className='d-flex gap-1'>
                                    //     <div className='w-100 flex-grow-1 d-flex align-items-center'>{newShiftButton}</div>
                                    //     <div className='w-100 flex-grow-1 d-flex align-items-center'>{newShiftButton}</div>
                                    // </div>
                                    newShiftButton
                                )
                                : (
                                    <div
                                        className={`small-add-button`}
                                        onClick={() => {
                                            setShiftSideBar(!shiftSideBar);
                                            saveEmployeeName(rowData.preferredName);
                                            setSelectedDay(day);
                                            setData(rowData);
                                        }}
                                    >
                                        <i className="ri-add-line"></i>
                                    </div>
                                )
                            }
                        </>
                    ) : null}
                </span>
            );
        } else {
            return schedule.actionStatus === 'draft' && !isDateOlder ? (
                <span key={`${rowData?.id}-${day}-${date}`}>
                    {timeOffText}
                    <div
                        className={timeOffText ? 'small-add-button' : 'scheduler-cell-body'}
                        onClick={() => {
                            setShiftSideBar(!shiftSideBar)
                            saveEmployeeName(rowData.preferredName)
                            setSelectedDay(day)
                            setData(rowData)
                        }}
                    >
                        <i className="ri-add-line"></i>
                    </div>
                </span >
            ) : timeOffText
        }
    }

    const getAmPmStaffCount = (day: string) => {
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
        const filteredStaffs = staffsWithSchedule?.staffs.filter(staff => Object.keys(staff.shifts).length > 0)
        const filteredStaffsByDay = filteredStaffs?.filter(staff => staff.shifts[date]?.length > 0)

        let amCount = 0;
        let pmCount = 0;

        filteredStaffsByDay?.forEach(staff => {
            const shifts = staff?.shifts[date];
            const amShifts = shifts?.filter(shift => shift.assignedShifts.startTime.includes('AM'));
            const pmShifts = shifts?.filter(shift => shift.assignedShifts.startTime.includes('PM'));
            amCount += amShifts?.length ? 1 : 0;
            pmCount += pmShifts?.length ? 1 : 0;
        })
        return {am: amCount, pm: pmCount}
    }

    const renderColumns = () => {
        return days.map((day, index) => {
            const customEvent = scheduleMetadata?.customEvents?.find((event) => event.dayName.toLowerCase() === day.toLowerCase())
            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
            const isHolidays = holidays.filter((holiday) => isDateInRange({ date: date, startDate: holiday.startDate, endDate: holiday.endDate })).length > 0
            const isMandatory = mandatoryDays.filter((mandatoryDay) => isDateInRange({ date: date, startDate: mandatoryDay.startDate, endDate: mandatoryDay.endDate })).length > 0
            const amPmCount = amPmView ? getAmPmStaffCount(day) : null;
            return (
                <Column
                    key={index}
                    headerClassName="workTimeScheduleHeader p-0"
                    header={(headerData) => (
                        <>
                            <CustomEventForm
                                parent='web'
                                scheduleId={scheduleId}
                                scheduleMetadata={scheduleMetadata}
                                customEvent={customEvent ?? {} as CustomEvent}
                                scheduleMutation={scheduleMutation}
                                onSuccess={onSuccess}
                                onError={onError}
                                day={day}
                                selectedWeek={selectedWeek}
                                actionStatus={schedule.actionStatus}
                                staffingRequirement={staffingRequirement}
                                staffingRequirementWithMinStaff={staffingRequirementWithMinStaff}
                                headerData={headerData}
                                holidays={holidays}
                                mandatoryDays={mandatoryDays}
                                isDateOlder={isDateOlder}
                            />
                            {amPmView && (
                                <div className={`${isHolidays && 'holiday-header'} ${isMandatory && 'mandatory-day'}`}>                                
                                    <Divider className='p-0 m-0 py-1' />
                                    <div className='w-100 row' style={{ height: '30px' }}>
                                        <div className='col-6 d-flex align-items-center justify-content-center'>
                                            <span>AM</span>
                                            <Badge 
                                                value={amPmCount?.am} 
                                                style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }} 
                                                className='ms-1' 
                                            />
                                        </div>
                                        <div className='col-6 d-flex align-items-center justify-content-center'>
                                            <span>PM</span>
                                            <Badge 
                                                value={amPmCount?.pm} 
                                                style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }} 
                                                className='ms-1' 
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    headerStyle={{ height: `${amPmView ? '150px' : '110px'}` }}
                    body={(data) => {
                        const parsedDate = getDateOfWeekday({ selectedWeek, day, format: 'YYYY-MM-DD' })
                        const dropId = `${data?.id}_${day}_${parsedDate}`;

                        const { shifts, unavailableDays, availabilities } = data
                        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' })
                        const availabilityText = getStaffAvailabilityForTheDay({ day, availabilities, selectedWeek, type: 'schedule' })
                
                        const unavailabilityArray = unavailableDays.filter((unavailableDay: any) => isDateInRange({ date: date, startDate: unavailableDay.startDate, endDate: unavailableDay.endDate }))
                        const unavailability = unavailabilityArray.filter((item: any) => (item.actionStatus === 'APPROVED' || item.actionStatus === 'PENDING'))
                
                        const timeOffText = getTimeOffMessage({ unavailability: unavailability, type: 'schedule', actionButton: true }, date)?.length
                            ? true
                            : availabilityText
                        const shiftsArray = shifts[date]

                        return <ShiftDroppable
                            key={dropId}
                            id={dropId}
                            content={cellBodyTemplate({ day, rowData: data })}
                            isLoading={(shiftDragDropMutation.isLoading || blockLoading) && shiftDroppedId === dropId}
                            danger={!!timeOffText && shiftsArray?.length}
                        />
                    }}
                    className={`shift-column p-0`}
                    //style={{ width: '12.5%' }}
                    style={{ minWidth: `${amPmView ? '15rem' : '12rem'}` }}
                />
            )
        })
    }

    const tableHeader = () => {
        return (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center" style={{ minWidth: `${amPmView ? '15rem' : '12rem'}`, height: `${amPmView ? '150px' : '110px'}` }}>
                <div className='w-100 px-4 d-flex justify-content-start align-items-center' style={{ height: '60px' }}>
                    <Checkbox
                        inputId="select-all-users"
                        name="select-all"
                        onChange={(e) => e.checked ? setSelectedUsers(allUsers) : setSelectedUsers([])}
                        checked={selectedUsers.length !== 0 && selectedUsers.length === allUsers.length}
                    />
                    <label htmlFor="select-all-users" className="mx-2">
                        <JTranslation text={"Select All"} />
                    </label>
                </div>
                <Divider className='p-0 m-0 py-1' />
                <div className='w-100 d-flex justify-content-between align-items-center px-4' style={{ height: '43px' }}>
                    <JTranslation text={"Events"} typeCase='pascal' />
                    <Badge className="ms-2" value={totalEvents} />
                </div>

                {amPmView && (
                    <>
                        <Divider className='p-0 m-0 py-1' />
                        <div className='w-100 d-flex justify-content-between align-items-center px-4' style={{ height: '33px' }}>
                            <JTranslation text={"Shifts"} typeCase='pascal' />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const onDragOver = async (message: string, variant: string) => {
        setBlockLoading(true);
        setToastMessage({ message, variant, show: true });
        await queryClient.refetchQueries([STAFFS_WITH_SCHEDULES_AND_SHIFT]);
        setBlockLoading(false);
        setShiftDroppedId(null);
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
                if (!event) return;

                const { active, over } = event;
                if (!active || !over) return;

                const [currentStaffId, currentShiftId, currentDay, currentParsedDate] = (active.id as string).split('_');
                const [overStaffId, overDay, overParsedDate] = (over.id as string).split('_');

                // if dragged to the same day, return
                if (currentStaffId === overStaffId && currentDay === overDay) return;

                // check if the shift is dragged already exists
                const checkIfShiftExist = checkIfStaffHasSameShift(event, tableData)
                if (checkIfShiftExist) {
                    setToastMessage({ message: 'Shift times overlap with another shift in this or other roles', variant: 'warning', show: true });
                    return;
                };

                // manually update the shifts and reload the page
                const updatedTableData = manualUpdateDragDropShift(event, tableData);
                setTableData(updatedTableData);
                setShiftDroppedId((over?.id as string) ?? null)

                updateDragDropShift(
                    shiftDragDropMutation,
                    scheduleId,
                    {
                        sourceShiftId: currentShiftId,
                        targetTenantStaffId: overStaffId,
                        targetDate: overParsedDate
                    },
                    onDragOver,
                    onDragOver
                )
            }}
        >
            {/* Assign shifts to multiple staffs */}
            {!!assignStaffShift &&
                <AddMultipleShiftAssignmentModal
                    key={'add-multiple-shift-' + assignStaffShift?.id}
                    selectedShift={assignStaffShift}
                    staffList={staffs}
                    visible={!!assignStaffShift}
                    setVisible={setAssignStaffShift}
                    scheduleId={scheduleId}
                    setToastMessage={setToastMessage}
                />
            }

            {/* Delete staff shift */}
            {!!deleteStaffShift &&
                <DeleteShiftModal
                    key={'delete-shift-' + deleteStaffShift?.id}
                    selectedShift={deleteStaffShift}
                    staffList={staffs}
                    visible={!!deleteStaffShift}
                    setVisible={setDeleteStaffShift}
                    scheduleId={scheduleId}
                    setToastMessage={setToastMessage}
                />
            }

            <div className="card h-100">
                <DataTable
                    value={tableData}
                    showGridlines
                    scrollable
                    scrollHeight='100%'
                    className="work-time-schedule-table d-flex h-100 flex-column"
                    loading={blockLoading ? false : isLoading}
                    pt={{
                        loadingOverlay: {
                            style: { zIndex: 3 }
                        }
                    }}
                >
                    <Column
                        field="day"
                        className="overflow-ellipsis"
                        header={tableHeader()}
                        headerClassName='p-0'
                        body={staffNameTemplate}
                        style={{ minWidth: `${amPmView ? '15rem' : '12rem'}` }}
                        headerStyle={{ height: `${amPmView ? '150px' : '110px'}` }}
                        frozen
                        pt={{
                            headerTitle: { style: { width: '100%' } }
                        }}
                    />
                    {renderColumns()}
                </DataTable>
            </div>
        </DndContext>
    )
}

export default React.memo(ShiftScheduler)
